import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, CalendarClock, PartyPopper } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

export default function RotaTab({ restaurant }) {
  const [anchor, setAnchor] = useState(new Date().toISOString().slice(0, 10));
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [covers, setCovers] = useState(0);
  const [celebrations, setCelebrations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ staff_email: "", date: "", start: "09:00", end: "17:00", hourly_rate: "12", notes: "" });

  const weekDates = useMemo(() => {
    const d = new Date(anchor + "T00:00:00");
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(monday);
      x.setDate(monday.getDate() + i);
      return x.toISOString().slice(0, 10);
    });
  }, [anchor]);

  const load = () => {
    api.get(`/restaurants/${restaurant.id}/shifts`, { params: { from_date: weekDates[0], to_date: weekDates[6] } })
      .then((r) => setShifts(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/bookings`)
      .then((r) => {
        const week = r.data.filter((b) => weekDates.includes(b.date) && b.status !== "cancelled");
        setCovers(week.reduce((n, b) => n + b.party_size, 0));
        setCelebrations(week.filter((b) => b.occasion).sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)));
      }).catch(() => {});
  };
  useEffect(() => { load(); }, [weekDates[0], restaurant.id]);
  useEffect(() => {
    api.get(`/restaurants/${restaurant.id}/staff`).then((r) => setStaff(r.data)).catch(() => {});
  }, [restaurant.id]);

  const shift = (n) => {
    const d = new Date(anchor + "T00:00:00");
    d.setDate(d.getDate() + n * 7);
    setAnchor(d.toISOString().slice(0, 10));
  };

  const addShift = async () => {
    const member = staff.find((s) => s.email === form.staff_email);
    try {
      await api.post(`/restaurants/${restaurant.id}/shifts`, {
        ...form, staff_name: member?.name || form.staff_email, role: member?.role || "",
        hourly_rate: Number(form.hourly_rate),
      });
      toast.success("Shift added");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeShift = async (sid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/shifts/${sid}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const hoursOf = (s) => {
    const [sh, sm] = s.start.split(":").map(Number);
    const [eh, em] = s.end.split(":").map(Number);
    return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
  };
  const totalHours = shifts.reduce((n, s) => n + hoursOf(s), 0);
  const laborCost = shifts.reduce((n, s) => n + hoursOf(s) * (s.hourly_rate || 0), 0);

  return (
    <div data-testid="rota-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <button data-testid="rota-prev" onClick={() => shift(-1)} aria-label="Previous week"
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <p className="font-outfit font-medium">
            Week of {new Date(weekDates[0] + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })}
          </p>
          <button data-testid="rota-next" onClick={() => shift(1)} aria-label="Next week"
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <Button data-testid="add-shift-btn" size="sm" className="rounded-full" onClick={() => { setForm({ ...form, date: weekDates[0] }); setOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Add shift
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
        {weekDates.map((d) => {
          const dayShifts = shifts.filter((s) => s.date === d);
          const dObj = new Date(d + "T00:00:00");
          const isToday = d === new Date().toISOString().slice(0, 10);
          return (
            <div key={d} data-testid={`rota-day-${d}`}
              className={`rounded-xl border p-3 min-h-[140px] ${isToday ? "border-primary/60 bg-primary/5" : "border-border/60 bg-card"}`}>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{dObj.toLocaleDateString("en-US", { weekday: "short" })}</p>
              <p className="font-outfit font-medium">{dObj.getDate()}</p>
              <div className="mt-2 space-y-1.5">
                {dayShifts.map((s) => (
                  <div key={s.id} data-testid={`shift-${s.id}`} className="rounded-lg border border-border/40 px-2 py-1.5 text-xs group">
                    <div className="flex items-center justify-between">
                      <p className="font-medium truncate">{s.staff_name}</p>
                      <button data-testid={`shift-del-${s.id}`} onClick={() => removeShift(s.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-muted-foreground">{s.start}–{s.end}{s.role ? ` · ${s.role}` : ""}</p>
                  </div>
                ))}
                {dayShifts.length === 0 && <p className="text-[10px] text-muted-foreground/60">—</p>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-border/60 bg-card p-5 flex flex-wrap gap-x-10 gap-y-2 text-sm" data-testid="rota-summary">
        <span className="flex items-center gap-2 text-muted-foreground"><CalendarClock className="w-4 h-4 text-primary" /> Week summary</span>
        <span>Scheduled: <strong className="text-foreground">{totalHours.toFixed(1)}h</strong></span>
        <span>Est. labor: <strong className="text-foreground">£{laborCost.toFixed(2)}</strong></span>
        <span>Covers booked: <strong className="text-foreground">{covers}</strong></span>
        <span>Labor / cover: <strong className="text-foreground">£{covers ? (laborCost / covers).toFixed(2) : "0.00"}</strong></span>
      </div>

      {celebrations.length > 0 && (
        <div className="mt-4 rounded-2xl border border-amber-400/40 bg-amber-400/5 p-5" data-testid="celebrations-card">
          <p className="text-sm font-medium flex items-center gap-2">
            <PartyPopper className="w-4 h-4 text-amber-400" /> Celebration prep — {celebrations.length} this week
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {celebrations.map((b) => (
              <span key={b.id} data-testid={`celebration-${b.id}`}
                className="text-xs rounded-full border border-amber-400/40 px-3 py-1.5">
                {new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · {b.time} — {b.name} · {b.occasion} · {b.party_size} guests
              </span>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Give the team a heads-up — a candle or card goes a long way.</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border/60">
          <DialogHeader><DialogTitle className="font-outfit">Add shift</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2" data-testid="shift-form">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Staff member</Label>
              <Select value={form.staff_email} onValueChange={(v) => setForm({ ...form, staff_email: v })}>
                <SelectTrigger data-testid="shift-staff-select" className="mt-1.5 bg-background border-border/60">
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => <SelectItem key={s.email} value={s.email}>{s.name} ({s.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Date</Label>
              <Input data-testid="shift-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1.5 bg-background border-border/60" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Start</Label>
                <Input data-testid="shift-start" type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })}
                  className="mt-1.5 bg-background border-border/60" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">End</Label>
                <Input data-testid="shift-end" type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })}
                  className="mt-1.5 bg-background border-border/60" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">£/hour</Label>
                <Input data-testid="shift-rate" type="number" min="0" step="0.5" value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="mt-1.5 bg-background border-border/60" />
              </div>
            </div>
            <Button data-testid="shift-save-btn" onClick={addShift} disabled={!form.staff_email || !form.date} className="w-full rounded-full">
              Add shift
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
